import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import TopHeroTemplate from "@/components/layout/TopHeroTemplate";
import { Card, CardContent } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { MapPin, Phone, Mail, Globe, Handshake, Share2, Filter, ArrowUpDown, Ticket } from "lucide-react";
import { useAuthState } from "@/hooks/useAuthState";
import { PartnerService, Partner } from "@/services/partnerService";
import { VoucherService } from "@/services/voucherService";
import { getBannerImageUrl, getPartnerLogoUrl } from "@/config/api";
import { BannerService, Banner } from "@/services/bannerService";
import heroMuroAlto1 from "@/assets/hero-muro-alto-1.jpg";
import heroMuroAlto2 from "@/assets/hero-muro-alto-2.jpg";
import heroMuroAlto3 from "@/assets/hero-muro-alto-3.jpg";
import heroMuroAlto4 from "@/assets/hero-muro-alto-4.jpg";
import heroMuroAlto5 from "@/assets/hero-muro-alto-5.jpg";

const PartnerSearch = () => {
  const navigate = useNavigate();
  const { isAuthenticated, userType, userName, userPhoto, userId } = useAuthState();

  // Estados
  const [partners, setPartners] = useState<Partner[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [serviceTypeFilter, setServiceTypeFilter] = useState<string>("");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc" | "random">("random");
  const [partnersWithVouchers, setPartnersWithVouchers] = useState<Set<number>>(new Set());
  const [principalBanners, setPrincipalBanners] = useState<Banner[]>([]);

  // Array de imagens de Muro Alto para o banner rotativo
  const heroImages = [heroMuroAlto1, heroMuroAlto2, heroMuroAlto3, heroMuroAlto4, heroMuroAlto5];
  
  // Seleciona uma imagem aleatória a cada render (refresh da página)
  const randomHeroImage = useMemo(() => {
    return heroImages[Math.floor(Math.random() * heroImages.length)];
  }, []);
  const randomPrincipalBanner = useMemo(() => {
    if (principalBanners.length > 0) {
      return principalBanners[Math.floor(Math.random() * principalBanners.length)];
    }
    return null;
  }, [principalBanners]);

  useEffect(() => {
    const loadPrincipalBanners = async () => {
      try {
        const principal = await BannerService.getBannersByType("principal");
        setPrincipalBanners(principal);
      } catch (error) {
        console.error("Erro ao carregar banner principal na página de parceiros:", error);
      }
    };
    loadPrincipalBanners();
  }, []);

  // Carregar parceiros e verificar quais têm vouchers ativos
  useEffect(() => {
    const loadPartners = async () => {
      try {
        setLoading(true);
        setError(null);
        const partnersData = await PartnerService.getAllPartners();
        setPartners(partnersData);

        // Verificar quais parceiros têm vouchers ativos
        const partnersWithActiveVouchers = new Set<number>();
        for (const partner of partnersData) {
          try {
            const vouchers = await VoucherService.getVouchersByPartner(partner.id);
            const today = new Date();
            today.setHours(0, 0, 0, 0); // Resetar horas para comparar apenas datas
            
            const hasActiveVoucher = vouchers.some(voucher => {
              // Verificar se está ativo e não foi excluído
              if (!voucher.is_active || voucher.deleted_at) {
                return false;
              }
              
              // Verificar data de início (se informada)
              if (voucher.start_date) {
                const startDate = new Date(voucher.start_date);
                startDate.setHours(0, 0, 0, 0);
                if (today < startDate) {
                  return false; // Ainda não começou
                }
              }
              
              // Verificar data de fim (se informada)
              if (voucher.end_date) {
                const endDate = new Date(voucher.end_date);
                endDate.setHours(0, 0, 0, 0);
                if (today > endDate) {
                  return false; // Já expirou
                }
              }
              
              // Verificar limite de uso (se informado)
              if (voucher.usage_limit !== null && voucher.usage_count >= voucher.usage_limit) {
                return false; // Limite de uso excedido
              }
              
              return true;
            });
            if (hasActiveVoucher) {
              partnersWithActiveVouchers.add(partner.id);
            }
          } catch (err) {
            console.error(`Erro ao verificar vouchers do parceiro ${partner.name}:`, err);
          }
        }
        setPartnersWithVouchers(partnersWithActiveVouchers);
      } catch (error) {
        console.error('Erro ao carregar parceiros:', error);
        setError('Erro ao carregar parceiros. Tente novamente.');
      } finally {
        setLoading(false);
      }
    };

    loadPartners();
  }, []);

  // Obter tipos de serviço únicos dos parceiros
  const serviceTypes = useMemo(() => {
    const types = new Set<string>();
    partners.forEach(partner => {
      if (partner.service_type && partner.service_type.trim() !== '') {
        types.add(partner.service_type);
      }
    });
    return Array.from(types).sort();
  }, [partners]);

  // Filtrar e ordenar parceiros
  const filteredPartners = useMemo(() => {
    let filtered = [...partners];

    // Filtro por termo de busca
    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(partner =>
        partner.name.toLowerCase().includes(term) ||
        partner.address?.toLowerCase().includes(term) ||
        partner.description?.toLowerCase().includes(term) ||
        partner.service_type?.toLowerCase().includes(term)
      );
    }

    // Filtro por tipo de serviço
    if (serviceTypeFilter) {
      filtered = filtered.filter(partner => partner.service_type === serviceTypeFilter);
    }

    // Ordenação: alfabética se selecionada, caso contrário randomizar
    if (sortOrder === "asc" || sortOrder === "desc") {
      // Ordenação alfabética
      filtered.sort((a, b) => {
        const nameA = a.name.toLowerCase();
        const nameB = b.name.toLowerCase();
        if (sortOrder === "asc") {
          return nameA.localeCompare(nameB, 'pt-BR');
        } else {
          return nameB.localeCompare(nameA, 'pt-BR');
        }
      });
    } else {
      // Randomização usando Fisher-Yates shuffle
      for (let i = filtered.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [filtered[i], filtered[j]] = [filtered[j], filtered[i]];
      }
    }

    return filtered;
  }, [partners, searchTerm, serviceTypeFilter, sortOrder]);

  const handlePartnerClick = (partnerId: number) => {
    navigate(`/parceiro/${partnerId}`);
  };

  const handleVoucherClick = (e: React.MouseEvent, partnerName: string) => {
    e.stopPropagation(); // Prevenir o clique no card
    navigate(`/vouchers?partner=${encodeURIComponent(partnerName)}`);
  };

  return (
    <div className="min-h-screen bg-background">
      <TopHeroTemplate
        backgroundImage={
          randomPrincipalBanner
            ? getBannerImageUrl(randomPrincipalBanner.image_path)
            : randomHeroImage
        }
        backgroundColor={randomPrincipalBanner?.background_color || "transparent"}
        navbarProps={{
          isAuthenticated,
          isOwner: userType === "owner",
          userName,
          userPhoto,
          userId,
          userType,
          cleanHeader: true,
          rightActionsCard: true,
        }}
        sectionClassName="h-[42vh] min-h-[430px] sm:min-h-[460px] flex items-end pb-8"
        contentClassName="relative z-10 container mx-auto px-4 text-center mt-28 sm:mt-32 md:mt-24"
      >
        <h1 className="text-3xl sm:text-4xl md:text-6xl font-bold text-white mb-3 drop-shadow-lg">
          Nossos Parceiros
        </h1>
        <p className="text-base sm:text-xl md:text-2xl text-white/95 mb-2 drop-shadow-md">
          Conheça nossa rede de parceiros oficiais
        </p>
      </TopHeroTemplate>

      <section className="py-12 container mx-auto px-4">
        <div className="mb-8">
          <div className="mb-4">
            <div>
              <h2 className="text-3xl font-bold text-foreground mb-2">
                {filteredPartners.length} parceiro{filteredPartners.length !== 1 ? 's' : ''} encontrado{filteredPartners.length !== 1 ? 's' : ''}
              </h2>
              <p className="text-muted-foreground">
                Parceiros oficiais que oferecem os melhores serviços e benefícios
              </p>
            </div>
          </div>

          {/* Filtros e Busca */}
          <div className="flex flex-col md:flex-row gap-4">
            {/* Barra de busca */}
            <div className="flex-1">
              <div className="relative">
                <input
                  type="text"
                  placeholder="Buscar parceiros..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full px-4 py-2 pl-10 border border-input rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
                />
                <Handshake className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              </div>
            </div>

            {/* Filtro por tipo de serviço */}
            <div className="w-full md:w-64">
              <Select value={serviceTypeFilter || "all"} onValueChange={(value) => setServiceTypeFilter(value === "all" ? "" : value)}>
                <SelectTrigger>
                  <SelectValue placeholder="Todos os tipos" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos os tipos</SelectItem>
                  {serviceTypes.map((type) => (
                    <SelectItem key={type} value={type}>
                      {type}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Ordenação */}
            <div className="w-full md:w-48">
              <Select value={sortOrder} onValueChange={(value: "asc" | "desc" | "random") => setSortOrder(value)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="random">Aleatório</SelectItem>
                  <SelectItem value="asc">A-Z</SelectItem>
                  <SelectItem value="desc">Z-A</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>

        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
            <p className="text-muted-foreground">Carregando parceiros...</p>
          </div>
        ) : error ? (
          <div className="text-center py-12">
            <p className="text-destructive text-lg mb-2">Erro ao carregar</p>
            <p className="text-muted-foreground">{error}</p>
          </div>
        ) : filteredPartners.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-muted-foreground text-lg">Nenhum parceiro encontrado</p>
            <p className="text-muted-foreground">
              {searchTerm ? "Tente ajustar o termo de busca" : "Não há parceiros disponíveis no momento"}
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {filteredPartners.map((partner) => (
              <Card
                key={partner.id}
                className="group cursor-pointer overflow-hidden border shadow-soft hover:shadow-medium transition-all duration-300 hover:-translate-y-1"
                onClick={() => handlePartnerClick(partner.id)}
              >
                <div className="relative h-48 bg-gradient-to-br from-primary/10 to-primary/5 flex items-center justify-center p-6">
                  <img
                    src={getPartnerLogoUrl(partner.logo)}
                    alt={partner.name}
                    className="max-h-full max-w-full object-contain group-hover:scale-105 transition-transform duration-500"
                    onError={(e) => {
                      const target = e.target as HTMLImageElement;
                      target.style.display = 'none';
                      const fallback = target.parentElement?.querySelector('.logo-fallback');
                      if (fallback) {
                        (fallback as HTMLElement).style.display = 'flex';
                      }
                    }}
                  />
                  <div className="logo-fallback hidden absolute inset-0 items-center justify-center">
                    <Handshake className="h-16 w-16 text-primary/30" />
                  </div>
                  {/* Badge de voucher ativo */}
                  {partnersWithVouchers.has(partner.id) && (
                    <button
                      onClick={(e) => handleVoucherClick(e, partner.name)}
                      className="absolute top-2 right-2 bg-primary text-primary-foreground rounded-full p-2 shadow-lg hover:bg-primary/90 transition-colors z-10"
                      title="Ver vouchers deste parceiro"
                      aria-label="Ver vouchers"
                    >
                      <Ticket className="h-5 w-5" />
                    </button>
                  )}
                </div>

                <CardContent className="p-6">
                  <h3 className="font-semibold text-xl mb-4">
                    {partner.name}
                  </h3>

                  <div className="space-y-2">
                    {partner.email && (
                      <div className="flex items-center gap-2 text-sm text-muted-foreground">
                        <Mail className="h-4 w-4 flex-shrink-0" />
                        <span className="truncate">{partner.email}</span>
                      </div>
                    )}

                    {partner.phone && (
                      <div className="flex items-center gap-2 text-sm text-muted-foreground">
                        <Phone className="h-4 w-4 flex-shrink-0" />
                        <span>{partner.phone}</span>
                      </div>
                    )}

                    {partner.website && (
                      <div className="flex items-center gap-2 text-sm text-primary">
                        <Globe className="h-4 w-4 flex-shrink-0" />
                        <a
                          href={partner.website.startsWith('http') ? partner.website : `https://${partner.website}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={(e) => e.stopPropagation()}
                          className="hover:underline truncate"
                        >
                          {partner.website.replace(/^https?:\/\//, '')}
                        </a>
                      </div>
                    )}

                    {partner.social_media && (
                      <div className="flex items-center gap-2 text-sm text-primary">
                        <Share2 className="h-4 w-4 flex-shrink-0" />
                        <a
                          href={partner.social_media.startsWith('http') ? partner.social_media : `https://${partner.social_media}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={(e) => e.stopPropagation()}
                          className="hover:underline truncate"
                        >
                          {partner.social_media.replace(/^https?:\/\//, '')}
                        </a>
                      </div>
                    )}

                    {partner.address && (
                      <div className="flex items-start gap-2 text-sm text-muted-foreground">
                        <MapPin className="h-4 w-4 mt-0.5 flex-shrink-0" />
                        <span>{partner.address}</span>
                      </div>
                    )}
                  </div>

                  <div className="mt-4 pt-4 border-t">
                    <button
                      className="w-full text-primary hover:text-primary/80 font-medium text-sm transition-colors"
                      onClick={(e) => {
                        e.stopPropagation();
                        handlePartnerClick(partner.id);
                      }}
                    >
                      Ver detalhes →
                    </button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </section>

      <Footer />
    </div>
  );
};

export default PartnerSearch;

